import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { SortingState } from "@tanstack/react-table";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import StatusBadge from "Common/Components/StatusBadge/StatusBadge";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import LoadingSpinner from "components/LoadingSpinner";
import Colors, { MecordColors } from "Constants/Color";
import { app_modules } from "helpers/apps_modules_list";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import usePermission from "hooks/usePermission";
import { showCustomSwal, showLoading, hideLoading } from "helpers/swalHelper";
import React, { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import {
  exportApprovePurchaseRequestCSV,
  fetchApprovedPurchaseRequest,
  revertApprovePr,
} from "slices/Procurement/purchase-order/approved-pr/thunk";
import Swal from "sweetalert2";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import StatusChip from "pages/Enquiry/Components/StatusChip";
import Chip from "components/Chips/Chip";

type ApprovedPRProps = {
  handleTabChange?: (tabIndex: number) => void;
  onTableReady?: (table: any) => void;
  noWrapper?: boolean;
};
const ApprovedPR: React.FC<ApprovedPRProps> = () => {
  document.title = "Approved Purchase Request List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.purchase_management?.app_name,
    app_modules?.purchase_management?.modules?.purchase_order
  );

  const selectAP = createSelector(
    (state: any) => state.AReducer,
    (pr) => ({
      list: pr.list,
      rowCount: pr.rowCount,
      loading: pr.loading,
      error: pr.error,
    })
  );
  const { list, rowCount, loading, error } = useSelector(selectAP);

  const tableState = usePreservedTableState({
    key: TableKeys.PURCHASE_MANAGEMENT.APRPOVED_PR,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  const [tableInstance, setTableInstance] = useState<any>();
  // const [search, setSearch] = useState("");
  // const [searchInput, setSearchInput] = useState("");
  const [revertingId, setRevertingId] = useState<string | null>(null);

  // Memoize sort values to prevent unnecessary re-renders
  const sortField = useMemo(() => sorting[0]?.id ?? "", [sorting]);
  const sortDirection = useMemo(
    () => (sorting[0]?.desc ? "desc" : "asc"),
    [sorting]
  );

  // Memoize filterValue string to prevent unnecessary re-renders
  const filterValueString = useMemo(
    () => JSON.stringify(filterValue),
    [filterValue]
  );

  // Fetch data when dependencies actually change
  useEffect(() => {
    dispatch(
      fetchApprovedPurchaseRequest({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValueString,
  ]);

  const handleRevert = (pr_id: string) => {
    showCustomSwal({
      title: "Are you sure?",
      text: "You want to revert this Approved PR!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: MecordColors.ThemeBGColor,
      cancelButtonColor: "#f46a6a",
      confirmButtonText: "Yes, revert it!",
      cancelButtonText: "Cancel",
    }).then((result: any) => {
      if (result.isConfirmed) {
        // show loading modal and mark which PR is reverting
        setRevertingId(pr_id);
        showLoading("Reverting...");
        dispatch(revertApprovePr(pr_id))
          .unwrap()
          .then((res: any) => {
            hideLoading();
            setRevertingId(null);
            if (res) {
              showCustomSwal({
                title: "Reverted!",
                text: "Approved PR has been reverted.",
                icon: "success",
              });
              dispatch(
                fetchApprovedPurchaseRequest({
                  page: pageIndex + 1,
                  pageSize,
                  sort: sortField,
                  order: sortField ? sortDirection : "",
                  search,
                  filters: filterValue,
                })
              );
            }
          })
          .catch((error: any) => {
            hideLoading();
            setRevertingId(null);
            console.error("Failed to revert Approved PR", error);
            showCustomSwal({
              title: "Error",
              text: "Failed to revert Approved PR",
              icon: "error",
            });
          });
      }
    });
  };
  // Debouncing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    setData(list);
  }, [list]);

  const columns = useMemo(
    () => [
      // {
      //   header: "Sr No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      // },
      {
        header: "PR No.",
        accessorKey: "pr_no",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_REQUEST?.FILTER_DROPDOWN_APPROVED_PR(
          "pr_no"
        )}`,
        cell: ({ row }: { row: any }) => {
          return row.original.pr_no || "-";
        },
      },

      {
        header: "PR Date",
        accessorKey: "pr_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.pr_date} />
        ),
      },
      {
        header: "Initiated By",
        accessorKey: "initiated_by",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_REQUEST?.FILTER_DROPDOWN_APPROVED_PR(
          "initiated_by"
        )}`,
        cell: ({ row }: { row: any }) => {
          return row.original.initiated_by || "-";
        },
      },
      {
        header: "PR Status",
        accessorKey: "status",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_REQUEST?.FILTER_DROPDOWN_APPROVED_PR(
          "status"
        )}`,
        cell: ({ row }: { row: any }) => {
          const status: string = row.original.status;
          return <StatusChip status={row.original?.status} />;

          // let backgroundColor = "#ddd";
          // if (status?.toLowerCase() === "pending") {
          //   backgroundColor = "#FDE4B4";
          // }
          // if (status?.toLowerCase() === "approved") {
          //   backgroundColor = "#B7FAEB";
          // }

          // return (
          //   <StatusBadge
          //     status={status}
          //     bgColor={backgroundColor}
          //     color="#070707"
          //   />
          // );
        },
      },
      {
        header: "Reviewed By",
        accessorKey: "approved_by_user__username",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_REQUEST?.FILTER_DROPDOWN_APPROVED_PR(
          "approved_by_user__username"
        )}`,
        cell: ({ row }: { row: any }) => {
          const u = row.original?.approved_by_user;
          if (!u) return "-";
          const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
          return u.username || "-";
        },
      },
      {
        header: "Reviewed Date",
        accessorKey: "approved_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.approved_date} />
        ),
      },
      // {
      //   header: "Attachments",
      //   accessorKey: "pr_document",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.pr_document ? (
      //       <Link target="_blank" to={row.original.pr_document}>
      //         View Document
      //       </Link>
      //     ) : (
      //       "No Document"
      //     );
      //   },
      // },
           {
        header: "Notes",
        accessorKey: "notes",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const notes = row.original.notes;

          if (!notes) return "-";

          return (
            <Tooltip title={notes} arrow placement="top">
              <Box
                sx={{
                  maxWidth: 250,
                  display: "-webkit-box",
                  WebkitLineClamp: 4, // 👈 change to 3 if needed
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  cursor: "pointer",
                  lineHeight: "1.4em",
                  maxHeight: "5.6em", // 4 lines × lineHeight
                }}
              >
                {notes}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        header: "Tags",
        accessorKey: "tags",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const tags: string[] = row.original.tags || [];
          if (!tags.length) return "-";

          const maxVisible = 3;
          const visible = tags.slice(0, maxVisible);
          const remaining = tags.length - visible.length;
          const tooltipText = tags.join(", ");

          return (
            <Tooltip
              arrow
              placement="top"
              title={tooltipText}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "13px",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  minWidth: "150px",
                  cursor: "pointer",
                }}
              >
                {visible.map((tag: string) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
                {remaining > 0 && <Chip>+{remaining} more</Chip>}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.created_by_user;
          return (
            <NameWithDate
              name={user?.username}
              date={row.original.created_at}
            />
          );
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.updated_by_user;
          return (
            <NameWithDate
              name={user?.username}
              date={row.original.updated_at}
            />
          );
        },
      },

      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const data = row.original;
          const menuItems: any[] = [];
          const canRevert = (row.original.status).toLowerCase() === "approved"
          
          // Always show "View Details"
          menuItems.push({
            label: "View Details",
            icon: VisibilityIcon,
            permission: canView,
            onClick: () =>
              navigate(
                `/purchase-request-approved-pr-view-details/${data?.pr_id}`,
                {
                  state: { pr_id: data?.pr_id },
                }
              ),
            color: "primary",
          });

          // if (row.original.status?.toLowerCase() === "approved") {
          //   menuItems.push({
          //     label: "Create Purchase Order",
          //     icon: ShoppingBagIcon,
          //     permission: canCreate,
          //     onClick: () =>
          //       navigate(`/create-purchase-order/${data?.pr_id}`, {
          //         state: { pr_id: data?.pr_id },
          //       }),
          //     color: "primary",
          //   });
          // }
          if(canRevert){
            menuItems.push({
              icon: UndoIcon,
              label:
              revertingId === row.original.pr_id ? "Reverting..." : "Revert",
              permission: canUpdate,
              disabled: revertingId === row.original.pr_id,
              onClick: (pr: any) => {
                if (revertingId !== row.original.pr_id) handleRevert(pr?.pr_id);
              },
              color: "warning",
            });
          }
          return <DynamicMenuItem row={row.original} menuItems={menuItems} />;
        },
      },
    ],
    [dispatch, list, revertingId, canView, canCreate, canUpdate, navigate]
  );

  return (
    <div
      className="d-flex flex-column"
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden",
      }}
    >
      <div className="flex-shrink-0">
        <Row className="align-items-center mecord-search-row g-2">
          <SearchBox
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search..."
          />

          <Col
            xs={12}
            md={12}
            lg={8}
            xl={7}
            className="d-flex justify-content-end align-items-center gap-2"
          >
            {tableInstance && (
              <ColumnVisibilityDropdown
                columns={tableInstance.getAllLeafColumns()}
              />
            )}
            {canView && (
              <ExportButton
                onExport={async () => {
                  return await exportApprovePurchaseRequestCSV({
                    search,
                    filters: filterValue,
                  });
                }}
              />
            )}
          </Col>
        </Row>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: "100%",
          marginTop: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <LoadingSpinner />
        ) : (
          <LoaderErrorWrapper loading={loading} error={error}>
            <MasterTableContainer
              columns={columns}
              data={data || []}
              totalRecords={rowCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              colValue={setFilterValue}
              sorting={sorting}
              onSortingChange={setSorting}
              isBordered={false}
              isPagination={true}
              manualSorting={true}
              onTableReady={setTableInstance}
            />
          </LoaderErrorWrapper>
        )}
      </div>
    </div>
  );
};

export default ApprovedPR;
